// Glow Up — every question a child gets wrong is kept here until they have genuinely mastered it.
//
// Stages:  0 Dim      — ready to fix (due now)
//          1 Glowing  — fixed once; prove it again tomorrow
//          2 Glowing  — proved once; one more check four days later
//          3 Shining  — mastered
// A correct first try moves an item up one stage. Fixing it only after help (the feedback for the chosen answer and
// the hint) counts as "fixed" but not as "proved", so it goes to stage 1. A wrong answer at any stage sends it back to
// Dim, due again tomorrow (it was just shown with its full explanation, so asking again straight away proves nothing).
//
// Stored per child profile in localStorage. The question is snapshotted when it is saved, so a review works without
// the bank being loaded; long reading passages are stored once in `passages` and shared by every item that uses them.

import { profileKey } from '../components/shared/constants';

const KEY = 'lumio_glowup';
const DAY = 864e5;
const NEXT_DUE_DAYS = { 1: 1, 2: 4 }; // stage reached -> days until it is checked again
const MAX_ITEMS = 300;
export const STAGE_NAMES = ['Dim', 'Glowing', 'Glowing', 'Shining'];

function startOfDay(ts) {
  const d = new Date(ts);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}
// due at the start of the day `days` from now, so "tomorrow" means tomorrow, not 24 hours later
function dueIn(days, now) {
  return startOfDay(now) + days * DAY;
}

function hashText(s) {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return 'p' + (h >>> 0).toString(36) + s.length.toString(36);
}

function emptyStore() {
  return { v: 1, items: {}, passages: {} };
}

export function load(profileId) {
  try {
    const s = JSON.parse(localStorage.getItem(profileKey(KEY, profileId)) || 'null');
    if (s && s.v === 1 && s.items && s.passages) return s;
  } catch (_) {}
  return emptyStore();
}

function prune(store) {
  const ids = Object.keys(store.items);
  if (ids.length > MAX_ITEMS) {
    // drop mastered items first, then the least recently touched
    const order = ids
      .map((id) => store.items[id])
      .sort((a, b) => (b.stage === 3) - (a.stage === 3) || a.updated - b.updated);
    for (const it of order.slice(0, ids.length - MAX_ITEMS)) delete store.items[it.id];
  }
  const used = new Set(Object.values(store.items).map((it) => it.ph).filter(Boolean));
  for (const h of Object.keys(store.passages)) if (!used.has(h)) delete store.passages[h];
}

function save(profileId, store) {
  prune(store);
  try {
    localStorage.setItem(profileKey(KEY, profileId), JSON.stringify(store));
  } catch (_) {
    // storage full: keep the most important half and try once more
    const keep = Object.values(store.items).filter((it) => it.stage < 3).sort((a, b) => b.updated - a.updated).slice(0, MAX_ITEMS / 2);
    store.items = Object.fromEntries(keep.map((it) => [it.id, it]));
    prune(store);
    try { localStorage.setItem(profileKey(KEY, profileId), JSON.stringify(store)); } catch (_) {}
  }
}

/** Save a question the child just got wrong (or send it back to Dim if it is already saved). Returns true if new. */
export function recordMistake(profileId, q, ctx = {}, now = Date.now()) {
  if (!q || !q.id) return false;
  const store = load(profileId);
  const existing = store.items[q.id];
  const { passage, ...rest } = q;
  let ph = null;
  if (typeof passage === 'string' && passage) {
    ph = hashText(passage);
    store.passages[ph] = passage;
  }
  if (existing) {
    Object.assign(existing, { q: rest, ph, stage: 0, due: now, updated: now, wrongCount: (existing.wrongCount || 0) + 1 });
  } else {
    store.items[q.id] = {
      id: q.id, q: rest, ph,
      subject: ctx.subject || q.subject || '', year: ctx.year || (Array.isArray(q.year) ? q.year[0] : q.year) || '',
      examProgram: ctx.examProgram || '', topic: q.topic || '',
      stage: 0, due: now, added: now, updated: now, wrongCount: 1, shinedAt: null,
    };
  }
  save(profileId, store);
  return !existing;
}

function hydrate(store, it) {
  return it.ph && store.passages[it.ph] ? { ...it.q, passage: store.passages[it.ph] } : { ...it.q };
}

/** Items due for review now, Dim before Glowing, oldest first; each carries its full question as `question`. */
export function getDue(profileId, { now = Date.now(), limit = 10 } = {}) {
  const store = load(profileId);
  return Object.values(store.items)
    .filter((it) => it.stage < 3 && it.due <= now)
    .sort((a, b) => a.stage - b.stage || a.due - b.due)
    .slice(0, limit)
    .map((it) => ({ ...it, question: hydrate(store, it) }));
}

/**
 * Record a review. outcome: 'first' (right first try), 'helped' (right only after the feedback and hint),
 * 'wrong' (still wrong after help). Returns the item's new stage.
 */
export function recordReview(profileId, id, outcome, now = Date.now()) {
  const store = load(profileId);
  const it = store.items[id];
  if (!it) return null;
  if (outcome === 'first') it.stage = Math.min(3, it.stage + 1);
  else if (outcome === 'helped') it.stage = 1;
  else it.stage = 0;
  if (it.stage === 3) { it.due = null; it.shinedAt = now; } // never due again (null, not Infinity: JSON has no Infinity)
  else if (it.stage === 0) it.due = dueIn(1, now);
  else it.due = dueIn(NEXT_DUE_DAYS[it.stage], now);
  it.updated = now;
  save(profileId, store);
  return it.stage;
}

/** Counts for the piles, what is ready now, and what reached Shining in the last 7 days. */
export function getCounts(profileId, now = Date.now()) {
  const items = Object.values(load(profileId).items);
  const c = { dim: 0, glowing: 0, shining: 0, ready: 0, shiningThisWeek: 0, total: items.length };
  for (const it of items) {
    if (it.stage === 0) c.dim++;
    else if (it.stage < 3) c.glowing++;
    else c.shining++;
    if (it.stage < 3 && it.due <= now) c.ready++;
    if (it.stage === 3 && it.shinedAt && now - it.shinedAt < 7 * DAY) c.shiningThisWeek++;
  }
  return c;
}

/** When the next item becomes due (ms timestamp), or null if nothing is waiting. */
export function nextDueAt(profileId) {
  const due = Object.values(load(profileId).items).filter((it) => it.stage < 3).map((it) => it.due);
  return due.length ? Math.min(...due) : null;
}
